import { t } from "@lingui/core/macro";
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  addToast,
} from "@heroui/react";
import { api, queryClient } from "@/lib/api-client";
import { useEffect, useState } from "react";
import {
  Bell,
  CalendarCheck,
  MoreHorizontal,
  Trash2,
  User,
} from "lucide-react";
import type { PostWaitlistList } from "@/server-types";
import { useConfirmModal } from "@/Components/NextBase/useConfirmModal";
import { useClientFeature } from "@/hooks/useClientFeature";
import { BookGroupConfirmModal } from "./components/BookGroupConfirmModal";
import { NotifyCustomerModal } from "./components/NotifyCustomerModal";

type Entry = PostWaitlistList["response"]["entries"][number];

function invalidateWaitlistQueries() {
  queryClient.invalidateQueries({ queryKey: ["waitlist-list"] });
  queryClient.invalidateQueries({ queryKey: ["waitlist-stats"] });
}

const TERMINAL_STATUSES = new Set([
  "booked",
  "expired",
  "cancelled",
  "declined",
]);

export function WaitlistRowActions({
  entry,
  openCustomerCard,
  onBookSuccess,
}: {
  entry: Entry;
  openCustomerCard?: (customerId: number) => void;
  onBookSuccess?: () => void;
}) {
  const [isBookOpen, setIsBookOpen] = useState(false);
  const [isNotifyOpen, setIsNotifyOpen] = useState(false);

  const {
    mutateAsync: deleteEntryAsync,
    isPending: isDeletePending,
    processedErrorMessage: deleteErrorMessage,
    reset: resetDelete,
  } = api.waitlist.useDeleteEntry();

  const isTerminal = TERMINAL_STATUSES.has(entry.status);

  // "Rezerviraj termin" is the dashboard's group-appointment booking flow
  // (`BookGroupConfirmModal`). It only ever fires for entries with a
  // `groupAppointmentId`, and the underlying booking writes to a
  // prescheduled appointment — so without the `prescheduledAppointments`
  // feature the action shouldn't be offered at all. Existing group rows
  // (legacy data, or feature was just toggled off) still appear in the
  // table but the row simply gets the "view customer" / "remove" actions.
  const { isFeatureEnabled } = useClientFeature();
  const isPrescheduledEnabled = isFeatureEnabled("prescheduledAppointments");

  const isGroupEntry = entry.groupAppointmentId != null;

  // Group-only capacity check. A group entry waits on ONE specific
  // appointment, so booking/notifying it must respect that appointment's
  // remaining seats. Regular entries have no `groupAppointment` — they
  // wait for any slot matching their date/time/service window — so this
  // returns false for them and regular actions must NOT be gated on it
  // (see `canNotify`). The server re-checks group capacity on the booking
  // path; the client gate just keeps the dropdown truthful.
  const hasFreeCapacity = (() => {
    const appt = entry.groupAppointment;
    if (!appt) return false;
    return appt.currentAttendance + entry.numberOfSeats <= appt.capacity;
  })();

  // Match the documented intent on `isPrescheduledEnabled` above: the
  // action targets group entries only AND the prescheduled feature must be
  // on. Previously this used `!isGroupEntry`, which inverted the rule —
  // regular entries got a visible-but-always-disabled button and group
  // entries were hidden unless prescheduled was on (the inverse of the
  // intent).
  const showBookAction = isPrescheduledEnabled && isGroupEntry;
  const canBook = !isTerminal && isGroupEntry && hasFreeCapacity;
  // Group entries notify against one specific appointment, so a seat must
  // be free or the customer's claim would 409. Regular entries have no
  // fixed appointment — the operator-initiated notify just sends a claim
  // link and availability is resolved when the customer opens it. The
  // server's `notifyRegularEntry` deliberately skips an upfront probe
  // (it's expensive and slot state shifts before the claim anyway), so
  // gating the regular notify on `hasFreeCapacity` here only ever
  // disabled it permanently.
  const canNotify =
    !isTerminal &&
    entry.status === "waiting" &&
    (!isGroupEntry || hasFreeCapacity);
  // Without a bookable form of this entry's kind (prescheduled vs not) the
  // server refuses the send — no claim URL can be built — so don't offer the
  // action at all rather than showing it and failing on press. The flag is
  // resolved per entry on the list response; default to shown for any older
  // response that doesn't carry it.
  const showNotifyAction = entry.hasBookableForm ?? true;

  const customerFullName =
    `${entry.customer.name ?? ""} ${entry.customer.lastName ?? ""}`.trim();

  const handleConfirmRemove = async () => {
    await deleteEntryAsync({ waitlistId: entry.waitlistId });
    addToast({ title: t`Odstranjeno`, color: "success" });
    invalidateWaitlistQueries();
  };

  const handleOpenNotifyModal = () => {
    setIsNotifyOpen(true);
  };

  const handleNotifySuccess = () => {
    invalidateWaitlistQueries();
  };

  const handleBookClose = () => {
    setIsBookOpen(false);
    invalidateWaitlistQueries();
  };

  const {
    open: openRemoveConfirm,
    isOpen: isRemoveConfirmOpen,
    modal: removeConfirmModal,
  } = useConfirmModal({
    title: t`Odstrani iz čakalne vrste`,
    content: (
      <p className="text-sm">
        {customerFullName
          ? t`Ali ste prepričani, da želite stranko ${customerFullName} odstraniti iz čakalne vrste? Te akcije ni mogoče razveljaviti.`
          : t`Ali ste prepričani, da želite ta vpis odstraniti iz čakalne vrste? Te akcije ni mogoče razveljaviti.`}
      </p>
    ),
    errorMessages: deleteErrorMessage
      ? [{ message: deleteErrorMessage }]
      : undefined,
    confirmProps: {
      label: t`Odstrani`,
      isLoading: isDeletePending,
      onClick: handleConfirmRemove,
    },
  });

  useEffect(() => {
    if (!isRemoveConfirmOpen) resetDelete();
  }, [isRemoveConfirmOpen, resetDelete]);

  const disabledKeys: string[] = [];
  if (!canBook) disabledKeys.push("book");
  if (!canNotify) disabledKeys.push("notify");

  return (
    <>
      <Dropdown placement="bottom-end">
        <DropdownTrigger>
          <Button isIconOnly size="sm" variant="light" aria-label={t`Dejanja`}>
            <MoreHorizontal size={18} />
          </Button>
        </DropdownTrigger>
        <DropdownMenu
          aria-label={t`Dejanja za vpis v čakalno vrste`}
          disabledKeys={disabledKeys}
        >
          {openCustomerCard ? (
            <DropdownItem
              key="view-customer"
              startContent={<User size={16} />}
              onPress={() => openCustomerCard(entry.customerId)}
            >
              {t`Odpri kartico stranke`}
            </DropdownItem>
          ) : null}
          {showBookAction ? (
            <DropdownItem
              key="book"
              onPress={() => setIsBookOpen(true)}
              startContent={<CalendarCheck size={16} />}
            >
              {t`Rezerviraj termin`}
            </DropdownItem>
          ) : null}
          {showNotifyAction ? (
            <DropdownItem
              key="notify"
              onPress={handleOpenNotifyModal}
              startContent={<Bell size={16} />}
            >
              {t`Obvesti stranko`}
            </DropdownItem>
          ) : null}
          <DropdownItem
            key="remove"
            className="text-danger"
            color="danger"
            onPress={openRemoveConfirm}
            startContent={<Trash2 size={16} />}
          >
            {t`Odstrani`}
          </DropdownItem>
        </DropdownMenu>
      </Dropdown>

      {removeConfirmModal}

      {/* Defensive: also gate the modal mount on the feature flag — even
          though the only `setIsBookOpen(true)` caller above is hidden when
          the feature is off, this prevents a stale `isBookOpen=true` from
          riding a feature-flag flip into rendering a flow the tenant no
          longer has access to. */}
      {isBookOpen && showBookAction && (
        <BookGroupConfirmModal
          entry={entry}
          isOpen={isBookOpen}
          onClose={handleBookClose}
          onSuccess={onBookSuccess}
        />
      )}

      {isNotifyOpen && showNotifyAction && (
        <NotifyCustomerModal
          entry={entry}
          isOpen={isNotifyOpen}
          onClose={() => setIsNotifyOpen(false)}
          onSuccess={handleNotifySuccess}
        />
      )}
    </>
  );
}
